import { useContext } from 'react';

import { LayerContext } from 'state/LayerContext';
import { useGetSegmentInfoQuery } from 'state/apiSlice';
import { useAppSelector } from 'state/hooks';
import {
  selectComparison,
  selectComparisonEndDate,
  selectCurrentProjectInfo,
  selectSegmentId,
  selectTargetDate,
  selectTargetEndDate,
} from 'state/workflowSlice';

import { ChartDataExpander } from 'features/chart/ChartDataExpander';
import {
  formatTargetSeriesName,
  formatTypicalSeriesName,
  getFormattedTime,
} from 'features/chart/nrtChartCommon';
import { isOnDay } from 'features/common/datetimeUtils';
import {
  ExportType,
  nrtCommonInfo,
  triggerCsvExport,
  useExportFilename,
} from 'features/common/exportUtils';
import { useRouteInfo } from 'features/common/hooks/useRouteInfo';
import { binEndFromTs } from 'features/common/slotsUtils';
import { formatDistance, formatSpeed, speedUnit } from 'features/common/utils';
import { useTargetDateMetrics } from 'features/workflow_traveltime/nrtTravelTimeCommon';

export function useSpeedsExportData(skip?: boolean) {
  const comparison = useAppSelector(selectComparison);
  const comparisonEndDate = useAppSelector(selectComparisonEndDate);
  const targetDate = useAppSelector(selectTargetDate);
  const targetEndDate = useAppSelector(selectTargetEndDate);
  const segmentId = useAppSelector(selectSegmentId);
  const userProject = useAppSelector(selectCurrentProjectInfo);
  const filename = useExportFilename(ExportType.Data);
  // cached so no need to memoize
  const { layer } = useContext(LayerContext);
  const { currentData: segmentData } = useGetSegmentInfoQuery(
    { layerId: layer, routeIds: [segmentId] },
    { skip: !layer || !segmentId || skip },
  );
  const { feature: routeFeature, savedRoute } = useRouteInfo(skip);

  const segment = segmentData && segmentData?.segments[segmentId];

  // typicalData from REST api
  const { metrics, comparisonData: comparisonSpeedData } = useTargetDateMetrics(
    skip,
    0,
  );

  const handleExport = () => {
    if (skip) return;
    const dataExpander = new ChartDataExpander(targetDate);
    const comparisonExpander = new ChartDataExpander(targetDate, comparison);
    const data = dataExpander.expandSpeeds(metrics, userProject.uses_metric);
    const expandedComparisonData = comparisonExpander.expandSpeeds(
      comparisonSpeedData,
      userProject.uses_metric,
    );
    const unit = speedUnit(userProject.uses_metric);

    const targetSeriesName = formatTargetSeriesName(
      targetDate,
      false,
      targetEndDate,
    );
    const typicalSeriesName = formatTypicalSeriesName(
      comparison,
      targetDate,
      userProject.uses_middle_east_day_types,
      false,
      undefined,
      true,
      comparisonEndDate,
    );

    const header = [
      `Time Start`,
      `Time End`,
      `${targetSeriesName} Speed (${unit})`,
      `${typicalSeriesName} Speed (${unit})`,
    ];

    const csv = [
      `Name, ${savedRoute ? savedRoute.routeName : segment.properties.name}`,
      `ID, ${segmentId}`,
      ...nrtCommonInfo(
        targetDate,
        comparison,
        segment,
        userProject.uses_middle_east_day_types,
        targetEndDate,
        comparisonEndDate,
      ),
      `Route/Segment length, ${formatDistance(
        routeFeature.properties.route_length,
        userProject.uses_metric,
      )}`,
      segment.properties.freeflow_speed_mph
        ? `Freeflow speed, ${formatSpeed(
            segment.properties.freeflow_speed_mph,
            userProject.uses_metric,
          )}`
        : null,
      '',
      '',
      header.join(', '),
    ];
    const format = (v) => {
      if (v || v === 0) {
        return Math.round(v).toString();
      } else {
        return '';
      }
    };
    for (let i = 0; i < data.length; i += 1) {
      const row = data[i];
      const compRow = expandedComparisonData[i];
      const time = row.x;
      const endtime = getFormattedTime(binEndFromTs(time, metrics.bin_size));
      if (isOnDay(time, targetDate)) {
        const csv_row = [
          getFormattedTime(time),
          endtime,
          format(row.y),
          format(compRow.y),
        ];
        csv.push(csv_row.join(', '));
      }
    }
    triggerCsvExport(filename, csv);
  };

  const isActive =
    !skip && !!metrics && !!comparisonSpeedData && !!routeFeature;

  return { handleExport, isActive };
}
